import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'

export default class extends Controller<HTMLTableElement> {
  static targets = ['checkbox', 'bulkActionWrapper', 'collapseToggle']
  static values = {
    collapseRows: Boolean,
    collapseRowsCount: Number,
    collapsed: { type: Boolean, default: true }
  }

  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly bulkActionWrapperTarget: HTMLElement
  declare readonly hasBulkActionWrapperTarget: boolean
  declare readonly collapseToggleTarget: HTMLButtonElement
  declare readonly hasCollapseToggleTarget: boolean

  declare collapseRowsValue: boolean
  declare collapseRowsCountValue: number
  declare collapsedValue: boolean

  stickyTds: NodeListOf<HTMLTableCellElement> | null = null

  connect(): void {
    if (this.hasBulkActionWrapperTarget) {
      this.#extendBulkActionWrapperToFillScreen()
      this.#disableAllBulkActionButtons()
      requestAnimationFrame(this.#updateBulkActionButtonAvailability.bind(this))
    }
    this.#makeStickyCellsReactiveToStickiness()

    if (this.collapseRowsValue) {
      // Start collapsed by default
      this.collapsedValue = true
      requestAnimationFrame(() => {
        this.#applyCollapse()
        this.#updateCollapseToggleLabel()
      })
    }
  }

  #extendBulkActionWrapperToFillScreen(): void {
    const table = this.element.querySelector('table')
    if (table === null) return
    const width = table.clientWidth + 'px'
    this.bulkActionWrapperTarget.style.width = width
  }

  #disableAllBulkActionButtons(): void {
    const buttons = this.bulkActionWrapperTarget.querySelectorAll('button')
    buttons.forEach(button => {
      button.disabled = true
    })
  }

  #makeStickyCellsReactiveToStickiness(): void {
    this.stickyTds = this.element.querySelectorAll('td.sticky, th.sticky')
    if (this.stickyTds[0] !== null) {
      const innerTableWrapper = this.element.querySelector('table')?.parentElement
      if (innerTableWrapper === null || innerTableWrapper === undefined) return
      innerTableWrapper?.addEventListener('scroll', debounce(this.scrollTable.bind(this), 25))
    }
  }

  #updateBulkActionButtonAvailability(): void {
    const isAnySelected = this.checkboxTargets.some(checkbox => checkbox.checked)
    const buttons = this.bulkActionWrapperTarget.querySelectorAll('button')
    const shouldDisable = !isAnySelected
    buttons.forEach(button => {
      button.disabled = shouldDisable
    })

    // TODO: Maybe put here the routine to update the bulk action forms with selected ids
  }

  // MARK: Event Handlers

  selectAllClick(): void {
    const visibleCheckboxes = this.checkboxTargets.filter(checkbox => {
      const row = checkbox.closest('tr') as HTMLTableRowElement | null
      if (row === null) return false
      const collapseHidden = row.dataset.collapseHidden === 'true'
      const classHidden = row.classList.contains('hidden')
      return !collapseHidden && !classHidden
    })

    const isAllSelected = visibleCheckboxes.every(checkbox => checkbox.checked)

    visibleCheckboxes.forEach(checkbox => {
      checkbox.checked = !isAllSelected
    })
    this.#updateBulkActionButtonAvailability()
    if (this.collapseRowsValue && this.collapsedValue) this.#applyCollapse()
  }

  selectOneClick(e: MouseEvent): void {
    if (e.target instanceof HTMLInputElement) {
      requestAnimationFrame(this.#updateBulkActionButtonAvailability.bind(this))
      if (this.collapseRowsValue && this.collapsedValue)
        requestAnimationFrame(this.#applyCollapse.bind(this))
    }
  }

  expandableRowClick(e: MouseEvent): void {
    const thisCell = e.target as HTMLTableCellElement | null
    const icon = thisCell?.querySelector('i')
    const thisRow = thisCell?.parentElement
    const nextRow = thisRow?.nextElementSibling as HTMLTableRowElement | null
    icon?.classList.toggle('rotate-90')
    if (nextRow === null) return
    const isOpening = nextRow.classList.contains('hidden')
    nextRow.classList.toggle('hidden')
    if (isOpening) {
      nextRow
        .querySelectorAll('turbo-frame[loading="lazy"]')
        .forEach(frame => frame.setAttribute('loading', 'eager'))
    }
  }

  scrollTable(e: Event): void {
    const innerTableWrapper = e.target as HTMLDivElement | null
    const wrapperLeft = innerTableWrapper?.getBoundingClientRect().left ?? 0
    this.stickyTds?.forEach(element => {
      const td = element
      const isSticking = td.getBoundingClientRect().left === wrapperLeft
      const classes = [
        'bg-gradient-to-l',
        'from-neutral-400',
        'via-neutral-400',
        'via-[3px]',
        'to-transparent',
        'to-[3px]'
      ]
      if (isSticking) {
        td.classList.add(...classes)
      } else {
        td.classList.remove(...classes)
      }
    })
  }

  submitBulkAction(e: Event): void {
    e.preventDefault()
    const form = e.target as HTMLFormElement | null
    if (form === null) return

    const key = form.dataset.key as string
    if (key === null) return

    const values = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)

    // delete all hidden fields possibly made before
    const hiddenFields = form.querySelectorAll('input[type="hidden"]')
    hiddenFields.forEach(field => {
      if ((field as HTMLInputElement).dataset.isGenerated === 'true') form.removeChild(field)
    })

    // make new hidden fields
    values.forEach(value => {
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = key + '[]'
      hiddenField.value = value
      hiddenField.dataset.isGenerated = 'true'
      form.appendChild(hiddenField)
    })

    form.submit()
  }

  inlineFilterValueChanged(e: Event): void {
    e.preventDefault()

    const filterValue = (e.target as HTMLInputElement).value.toLowerCase()
    const rows = this.element.querySelectorAll('tbody tr')

    rows.forEach(row => {
      const cells = row.querySelectorAll('td[data-inline-filter]')

      const checkbox = row.querySelector(
        'td:first-child input[type="checkbox"]'
      ) as HTMLInputElement

      const isChecked = checkbox?.checked ?? false

      let rowMatches = false
      cells.forEach(cell => {
        const cellText = cell.textContent?.toLowerCase()?.trim() ?? ''

        if (cellText.startsWith(filterValue)) {
          rowMatches = true
        }
      })

      if (rowMatches || isChecked) {
        row.classList.remove('hidden')
      } else {
        row.classList.add('hidden')
      }
    })

    if (this.collapseRowsValue) this.#applyCollapse()
  }

  // MARK: Collapse logic
  toggleCollapse(): void {
    if (!this.collapseRowsValue) return
    this.collapsedValue = !this.collapsedValue
    this.#applyCollapse()
    this.#updateCollapseToggleLabel()
  }

  #getMainRows(): HTMLTableRowElement[] {
    const all = Array.from(this.element.querySelectorAll('tbody > tr')) as HTMLTableRowElement[]
    return all.filter(r => !r.classList.contains('TABLE--EXPANDABLE_ROW_CONTENT'))
  }

  #getExpandableContentRow(mainRow: HTMLTableRowElement): HTMLTableRowElement | null {
    const next = mainRow.nextElementSibling as HTMLTableRowElement | null
    if (next && next.classList.contains('TABLE--EXPANDABLE_ROW_CONTENT')) return next
    return null
  }

  #applyCollapse(): void {
    const count = this.collapseRowsCountValue || 20
    const rows = this.#getMainRows()

    if (this.collapsedValue) {
      let shown = 0
      rows.forEach(row => {
        const checkbox = row.querySelector(
          'td:first-child input[type="checkbox"]'
        ) as HTMLInputElement | null
        const isChecked = checkbox?.checked ?? false
        const isFilteredHidden = row.classList.contains('hidden')
        const contentRow = this.#getExpandableContentRow(row)

        // Always clear previous collapse state first
        if (row.dataset.collapseHidden === 'true') {
          row.style.display = ''
          delete row.dataset.collapseHidden
        }
        if (contentRow && contentRow.dataset.collapseHidden === 'true') {
          contentRow.style.display = ''
          delete contentRow.dataset.collapseHidden
        }

        if (isFilteredHidden) return

        if (isChecked) {
          // Checked rows never hidden by collapsing and do not count toward the limit
          return
        }

        if (shown < count) {
          shown += 1
          return
        }

        // Collapse this row (and its expandable content row if present)
        row.style.display = 'none'
        row.dataset.collapseHidden = 'true'
        if (contentRow) {
          contentRow.style.display = 'none'
          contentRow.dataset.collapseHidden = 'true'
        }
      })
    } else {
      // Expand: unhide only rows hidden by collapse (respect inline filter)
      rows.forEach(row => {
        if (row.dataset.collapseHidden === 'true') {
          row.style.display = ''
          delete row.dataset.collapseHidden
        }
        const contentRow = this.#getExpandableContentRow(row)
        if (contentRow && contentRow.dataset.collapseHidden === 'true') {
          contentRow.style.display = ''
          delete contentRow.dataset.collapseHidden
        }
      })
    }
  }

  #updateCollapseToggleLabel(): void {
    if (!this.hasCollapseToggleTarget) return
    this.collapseToggleTarget.textContent = this.collapsedValue ? 'Show all' : 'Show less'
  }
}
